<template>
  <ManagerLayout>
    <div class="manager-feedback-playbacks">
      <h2 class="page-title">返信コメント再生履歴</h2>
      <p class="page-desc">
        誰が・いつ・先生からの返信コメントを何回再生したかを集計・一覧で確認できます。（feedback_events の play_started / play_ended を利用）
      </p>

      <!-- フィルタ -->
      <section class="manager-section filters-section">
        <div class="card">
          <h3 class="section-title">期間フィルタ</h3>
          <div class="filters">
            <label class="filter-item">
              <span class="filter-label">日付（から）</span>
              <input v-model="filterDateFrom" type="date" class="form-input" />
            </label>
            <label class="filter-item">
              <span class="filter-label">日付（まで）</span>
              <input v-model="filterDateTo" type="date" class="form-input" />
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="applyFilters">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="exportCsvLoading" @click="downloadPlaybacksCsv('summary')">
              {{ exportCsvLoading ? '取得中...' : '集計CSV' }}
            </button>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="exportCsvLoading" @click="downloadPlaybacksCsv('events')">
              {{ exportCsvLoading ? '取得中...' : 'イベント一覧CSV' }}
            </button>
          </div>
        </div>
      </section>

      <!-- 集計：ユーザー別再生完了回数 -->
      <section class="manager-section">
        <h3 class="section-title">集計（誰が何回再生したか）</h3>
        <div class="card">
          <div class="toolbar">
            <span class="note">再生完了（play_ended）回数で集計</span>
          </div>
          <div v-if="summaryLoading" class="loading-state">読み込み中...</div>
          <div v-else-if="!summary.length" class="empty-state">該当データがありません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="ユーザー別再生回数">
              <thead>
                <tr>
                  <th>ユーザー（名前）</th>
                  <th>メール</th>
                  <th class="num">再生完了回数</th>
                  <th>最終再生日時（JST）</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in summary" :key="row.userId" class="data-row">
                  <td>{{ row.fullName || '—' }}</td>
                  <td>{{ row.email }}</td>
                  <td class="num">{{ row.playEndedCount }}回</td>
                  <td>{{ formatDateTimeJst(row.lastPlayedAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- イベント一覧 -->
      <section class="manager-section">
        <h3 class="section-title">イベント一覧（いつ・誰が・再生開始/完了）</h3>
        <div class="card">
          <div class="toolbar">
            <span class="total-note">全 {{ eventsTotal }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="eventsLoading" @click="loadEvents">
              更新
            </button>
          </div>
          <div v-if="eventsLoading" class="loading-state">読み込み中...</div>
          <div v-else-if="!events.length" class="empty-state">該当するイベントがありません</div>
          <div v-else class="table-wrap events-table-wrap">
            <table class="data-table" aria-label="再生イベント一覧">
              <thead>
                <tr>
                  <th>日時（JST）</th>
                  <th>ユーザー（名前）</th>
                  <th>メール</th>
                  <th class="num">Day</th>
                  <th>種別</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="e in events" :key="e.id" class="data-row">
                  <td>{{ formatDateTimeJst(e.createdAt) }}</td>
                  <td>{{ e.userFullName || '—' }}</td>
                  <td>{{ e.userEmail }}</td>
                  <td class="num">{{ e.day }}</td>
                  <td>
                    <span class="event-badge" :class="e.eventType">{{ e.eventTypeLabel }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="events.length > 0 && events.length < eventsTotal" class="load-more">
            <button type="button" class="btn btn-secondary" :disabled="loadingMore" @click="loadMore">
              {{ loadingMore ? '読み込み中...' : 'さらに読み込む' }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''
const PAGE_SIZE = 200

const filterDateFrom = ref('')
const filterDateTo = ref('')
const summary = ref([])
const summaryLoading = ref(false)
const events = ref([])
const eventsTotal = ref(0)
const eventsLoading = ref(false)
const loading = ref(false)
const loadingMore = ref(false)
const eventsOffset = ref(0)
const exportCsvLoading = ref(false)

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

function buildQuery (params) {
  const p = new URLSearchParams()
  if (filterDateFrom.value) p.set('dateFrom', filterDateFrom.value + 'T00:00:00.000Z')
  if (filterDateTo.value) p.set('dateTo', filterDateTo.value + 'T23:59:59.999Z')
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && v !== '') p.set(k, String(v))
  })
  return p.toString()
}

async function downloadCsv (url, defaultFilename) {
  const token = await getToken()
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'ダウンロードに失敗しました')
  }
  const disposition = res.headers.get('Content-Disposition')
  const filename = disposition ? (disposition.match(/filename="([^"]+)"/)?.[1] || defaultFilename) : defaultFilename
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

const downloadPlaybacksCsv = async (format) => {
  exportCsvLoading.value = true
  try {
    const params = new URLSearchParams()
    params.set('format', format)
    if (filterDateFrom.value) params.set('dateFrom', filterDateFrom.value + 'T00:00:00.000Z')
    if (filterDateTo.value) params.set('dateTo', filterDateTo.value)
    const url = `${apiBase}/api/manager/export/feedback-playbacks?${params.toString()}`
    const defaultName = format === 'summary' ? 'feedback-playbacks-summary.csv' : 'feedback-playbacks-events.csv'
    await downloadCsv(url, defaultName)
  } catch (e) {
    alert(e.message || 'CSVの取得に失敗しました')
  } finally {
    exportCsvLoading.value = false
  }
}

const loadSummary = async () => {
  summaryLoading.value = true
  try {
    const token = await getToken()
    const qs = buildQuery({})
    const res = await fetch(`${apiBase}/api/manager/feedback-playbacks/summary?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      summary.value = []
      return
    }
    summary.value = data.summary || []
  } catch (_) {
    summary.value = []
  } finally {
    summaryLoading.value = false
  }
}

const loadEvents = async (offset = 0) => {
  if (offset === 0) eventsLoading.value = true
  try {
    const token = await getToken()
    const qs = buildQuery({ limit: PAGE_SIZE, offset })
    const res = await fetch(`${apiBase}/api/manager/feedback-playbacks?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      if (offset === 0) {
        events.value = []
        eventsTotal.value = 0
      }
      return
    }
    const list = data.events || []
    if (offset === 0) {
      events.value = list
      eventsTotal.value = data.total ?? 0
      eventsOffset.value = list.length
    } else {
      events.value = [...events.value, ...list]
      eventsOffset.value = offset + list.length
    }
  } catch (_) {
    if (offset === 0) {
      events.value = []
      eventsTotal.value = 0
    }
  } finally {
    if (offset === 0) eventsLoading.value = false
  }
}

const applyFilters = async () => {
  loading.value = true
  try {
    await Promise.all([loadSummary(), loadEvents(0)])
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  loadingMore.value = true
  try {
    await loadEvents(eventsOffset.value)
  } finally {
    loadingMore.value = false
  }
}

onMounted(() => {
  applyFilters()
})
</script>

<style scoped>
.manager-feedback-playbacks {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
  color: #334155;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-label {
  font-size: 0.8rem;
  color: #64748b;
}

.form-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.note,
.total-note {
  font-size: 0.9rem;
  color: #64748b;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.events-table-wrap {
  max-height: 50vh;
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.data-table th.num,
.data-table td.num {
  text-align: right;
}

.data-table thead th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-row:hover {
  background: #f8fafc;
}

.event-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

.event-badge.play_started {
  background: #dbeafe;
  color: #1e40af;
}

.event-badge.play_ended {
  background: #d1fae5;
  color: #065f46;
}

.load-more {
  margin-top: 1rem;
  text-align: center;
}
</style>
